import { useCallback, useEffect, useState } from 'react';
import { includes, isEqual, keys, pickBy } from 'lodash';
import queryString from 'query-string';
import { useLocation } from 'react-use';
import { getApiQueryFromPaginationQuery } from 'utils/routes';
import { useDispatch, useSelector } from 'react-redux';

const usePagination = ({ actionFn, selectorFn, defaultParams, extraArguments, sliceId, defaultSort, ...options }) => {
   const dispatch = useDispatch();
   const location = useLocation();
   const [params, setParams] = useState(defaultParams);
   const query = queryString.parse(location.search);
   const shouldDismissDefaultParams =
      options.shouldDismissDefaultParamsIfAnyParamIsSet &&
      keys(query).filter(key => !includes(['page', 'sort', 'order', 'search'], key)).length > 0;
   const aggregateQuery = getApiQueryFromPaginationQuery(
      queryString.stringify(
         pickBy({
            page: 1,
            sort: defaultSort,
            ...(!isEqual(params, defaultParams) || !shouldDismissDefaultParams ? params : {}),
            ...query,
         })
      )
   );

   const { data, loadingState, pagination } = useSelector(state => selectorFn(state, { aggregateQuery, sliceId }));

   const request = useCallback(() => {
      if (!actionFn) {
         return;
      }

      dispatch(actionFn({ params: aggregateQuery, sliceId, ...extraArguments }));
      // eslint-disable-next-line
   }, [dispatch, aggregateQuery, sliceId, extraArguments]);

   useEffect(() => {
      request();
      // eslint-disable-next-line
   }, [aggregateQuery, sliceId, extraArguments]);

   const setParam = param => setParams({ ...params, ...param });

   const skeletonLength = pagination
      ? pagination.page * pagination.perPage > pagination.total
         ? pagination.page * pagination.perPage - pagination.total
         : 5
      : 5;

   return {
      data,
      loadingState,
      pagination,
      setParam,
      setParams,
      skeletonLength,
      request,
   };
};

export default usePagination;
